import type { InvoiceEmailDetailResponse } from './types';

import {
  type DefaultError,
  queryOptions,
  type QueryFunctionContext,
  type UseSuspenseQueryOptions,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { ms } from 'ms';

import type { UUID } from '~/types/common';

import { fetchInvoiceEmail } from './fetchInvoiceEmail';
import { queryKeysInvoiceEmail } from './queryKeys';

/**
 * Generates query options for one invoice inbox email.
 *
 * @param emailId - Invoice email identifier.
 * @param options - Additional query options.
 * @returns Query options for the invoice email detail.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice-Emails/operation/get_invoice_email_invoice_emails__email_id__get
 */
function getInvoiceEmailQueryOptions<TData = InvoiceEmailDetailResponse>(
  emailId: UUID,
  options?: Omit<
    UseSuspenseQueryOptions<InvoiceEmailDetailResponse, DefaultError, TData>,
    'queryKey' | 'queryFn'
  >,
) {
  return queryOptions<InvoiceEmailDetailResponse, DefaultError, TData>({
    queryFn: (context: QueryFunctionContext) =>
      fetchInvoiceEmail(emailId, context),
    queryKey: queryKeysInvoiceEmail.getDetail(emailId),
    staleTime: ms('30s'),
    ...options,
  });
}

/**
 * Loads one invoice inbox email, including its bodies and attachments.
 *
 * @param emailId - Invoice email identifier.
 * @param options - Additional query options.
 * @returns TanStack Query result containing the invoice email detail.
 */
export function useQueryInvoiceEmail<TData = InvoiceEmailDetailResponse>(
  emailId: UUID,
  options?: Omit<
    UseSuspenseQueryOptions<InvoiceEmailDetailResponse, DefaultError, TData>,
    'queryKey' | 'queryFn'
  >,
) {
  return useSuspenseQuery(getInvoiceEmailQueryOptions<TData>(emailId, options));
}
